/**
 * Contract for POST /api/v1/products/bulkImageAnalyze — the photo-driven half
 * of the curator's bulk-upload flow (see NineSetupAnalyzePanel).
 *
 * Kept in its OWN file rather than folded into AiObject.ts on purpose. There
 * are three AI endpoints on this controller and the backend handover calls out
 * that they are easy to confuse:
 *
 *   bulkImageAnalyze     1–5 photos + context  → postable product drafts,
 *                                                photos stored, image↔product map
 *   fetchautofill        photos (first only)   → loose fields, no product, no storage
 *   generateBulkProducts seller text, no photos → drafts in a DIFFERENT shape
 *                                                (that one lives in AiObject.ts)
 *
 * Two rules govern everything below, both from the handover doc:
 *
 * 1. `AnalyzedProduct.draft` IS a CreateProductRq. It is never rebuilt, never
 *    mapped field-by-field into a local model, and never filtered. The curator
 *    edits the fields they touch and the object is posted back to
 *    bulkCreateProduct as-is. Every field copied by hand is a field that
 *    silently drops the day the backend adds one — which is why `draft.product`
 *    is typed as an open record here rather than enumerated.
 *
 * 2. Everything is keyed on `ref` (img_0, prod_0, …), never on array index.
 *    Refs are validated server-side against the real upload set, so a ref that
 *    arrives always exists and never appears under two products. Deleting a
 *    photo is then filtering a ref out of a list, with no renumbering anywhere.
 *
 * Nulls are serialised: there is no global NON_NULL inclusion on this service,
 * so `draft.product` comes back with every field present and most of them null.
 * A key being present does NOT mean it has a value.
 */

// ── Request ──

/** One storefront section the business offers. The model picks one per product.
 *  Never send "others" — it is always available as a fallback and adding it is
 *  a no-op. */
export interface AnalyzeSectionContext {
  name: string;
  subsections?: string[];
}

/**
 * The `context` part of the multipart request.
 *
 * Split in two by the backend: `sellerNotes` / `age` / `condition` / `sections`
 * are shown to the model, while everything below them is stamped onto every
 * draft and never reaches the prompt.
 *
 * Every field is optional to the CALL — it succeeds without them. But the ones
 * marked "→ draft" below come back named in `missingRequiredFields` when
 * absent, and bulkCreateProduct rejects the draft later. Send what you have and
 * let missingRequiredFields drive the prompt for the rest.
 */
export interface BulkImageAnalyzeContext {
  /** Turbo: deeper, business-aware intelligence. Sent only when on. */
  turbo?: boolean;
  /** The strongest non-image signal. If the seller typed anything, send it —
   *  a stated value beats what the photo suggests. */
  sellerNotes?: string;
  /** Years. Copied to product.age verbatim; the model never guesses it. */
  age?: number;
  /** Wins over the model's read of the photo when supplied. */
  condition?: string;
  sections?: AnalyzeSectionContext[];

  /**
   * → draft, and omitted by the Nine Setup flow — a curator building a business
   * page is not the seller.
   *
   * Omitting it costs nothing: `sellerId` is NOT required, and the create path
   * does not read it from the payload at all. `ProductService.createProduct`
   * derives it from the auth context only for a personal listing
   * (`businessId == null`); a business product, which is all this flow makes,
   * is stored without one by design. So there is nothing to prompt for here and
   * nothing a curator could usefully supply.
   */
  sellerId?: string;
  /** → draft */
  sellerName?: string;
  /** → draft. `listingType` here is used when the top-level one is absent. */
  business?: {
    businessId: string;
    name: string;
    listingType?: string;
  };
  /** → draft. Not derivable from a photo. */
  latitude?: number;
  /** → draft. Not derivable from a photo. */
  longitude?: number;
  /** → draft, e.g. "AUTO" */
  contactMode?: string;
  /** → draft, e.g. "new_product". Falls back to business.listingType. */
  listingType?: string;
}

// ── Response ──

/** ASSIGNED — a product uses it. UNASSIGNED — stored, but nothing was made from
 *  it. FAILED — never made it in (mediaResource and publicUrl are null). */
export type AnalyzedImageStatus = "ASSIGNED" | "UNASSIGNED" | "FAILED";

/** Identical to the entries inside draft.product.productMediaResources. */
export interface AnalyzedMediaResource {
  mediaType: string | null;
  objectPath: string | null;
  section: string | null;
  thumbNail: string | null;
}

export interface AnalyzedImage {
  /** img_0, img_1, … in upload order. The React key. */
  ref: string;
  /** Sanitised server-side. Worth showing so a curator can tell two similar
   *  shots apart. */
  originalFilename: string;
  status: AnalyzedImageStatus;
  /** Null when FAILED. */
  mediaResource: AnalyzedMediaResource | null;
  /** Goes straight into an <img src>. Null when FAILED. */
  publicUrl: string | null;
}

/**
 * A postable CreateProductRq.
 *
 * `product` is an open record BY DESIGN — see rule 1 in the file header. Do not
 * narrow it to a named interface: the whole point is that unknown fields
 * survive the round trip untouched.
 *
 * `unknown` rather than `any`, deliberately. Both let an arbitrary field ride
 * along, which is the requirement; only `unknown` still forces a reader to say
 * what it expects a field to be before using it. `any` would additionally have
 * turned every typo into a silent undefined at the exact call sites that render
 * this to a curator.
 */
export interface AnalyzedProductDraft {
  product: Record<string, unknown>;
}

export interface AnalyzedProduct {
  /** prod_0, prod_1, … */
  ref: string;
  /** 0–1. Blends "am I sure what this is" with "how much of details could I
   *  fill". Below ANALYZE_LOW_CONFIDENCE deserves a visible flag on the card. */
  confidence: number;
  /** Never empty, never shared with another product. */
  imageRefs: string[];
  /** Always a member of imageRefs. The hero shot. */
  primaryImageRef: string;
  /** Typed fields (UUIDs, coordinates) the draft could not fill. These cannot
   *  hold a placeholder string — drive the "complete before saving" state off
   *  this list. */
  missingRequiredFields: string[];
  draft: AnalyzedProductDraft;
}

/** Per-image and per-product problems that did NOT fail the call. Usually
 *  non-empty and usually fine. */
export interface AnalyzeErrorInformation {
  errorCode: string;
  errorDetail: string;
  /** Carries the offending photo on every code except
   *  IMAGE_ANALYZE_PRODUCT_DISCARDED, which has no imageRef. */
  additionalDetails?: { imageRef?: string } & Record<string, any>;
}

export interface BulkImageAnalyzeResponse {
  /** Quote it in bug reports — it keys every server log line for this call and
   *  names the storage folder the photos landed in. */
  analysisId: string;
  /** Upload order, every file included whatever happened to it. */
  images: AnalyzedImage[];
  /** Can be empty. */
  products: AnalyzedProduct[];
  /** Same set as `images` with status UNASSIGNED, handed over separately so the
   *  client doesn't have to filter. A photo silently vanishing is the worst
   *  outcome in this flow, so these are always surfaced. */
  unassignedImageRefs: string[];
  errors: AnalyzeErrorInformation[];
  /** Non-null ONLY on a total failure, and then the HTTP status is not 200. */
  error: string | null;
}

// ── Per-item error codes ──

/**
 * Arrive inside `errors` on an otherwise successful call. Copy is written for a
 * curator looking at one photo, not for a log — each says what to do next,
 * because "IMAGE_ANALYZE_TOO_LARGE" on a tile helps nobody.
 *
 * ANALYZE_FILE_MISSING is ours, not the backend's: after a page refresh the
 * picked File bytes are gone (see AnalyzeImageFileStore) while the analysis
 * state is rehydrated, so an image that never got analysed has nothing left to
 * send and must be re-picked.
 */
export const ANALYZE_ERROR_COPY: Record<string, string> = {
  IMAGE_ANALYZE_TYPE_UNSUPPORTED: "Not a JPEG or PNG. Convert it and re-upload.",
  IMAGE_ANALYZE_TOO_LARGE: "Over 5 MB. Compress it and re-upload.",
  IMAGE_ANALYZE_UNREADABLE: "This file is corrupt or truncated. Re-take the photo.",
  IMAGE_ANALYZE_UPLOAD_FAILED: "Storage rejected it. This is usually temporary — retry.",
  IMAGE_ANALYZE_IMAGE_UNASSIGNED: "No product could be identified from this photo.",
  IMAGE_ANALYZE_PRODUCT_DISCARDED: "A product was dropped — it referenced no usable photo.",
  ANALYZE_FILE_MISSING: "This photo was lost on refresh. Remove it and pick it again.",
};

/** Falls back to the server's own errorDetail, then to generic copy — an
 *  errorCode this build has never heard of still reads as something. */
export function analyzeErrorCopy(
  errorCode: string | null | undefined,
  errorDetail?: string | null
): string {
  if (errorCode && ANALYZE_ERROR_COPY[errorCode]) return ANALYZE_ERROR_COPY[errorCode];
  if (errorDetail) return errorDetail;
  return "This photo couldn't be analysed. Try again.";
}

// ── Client-side limits ──
//
// The first four mirror server limits, so a violation is caught before a round
// trip is spent on it. They are configurable per environment under
// `openai.image-analyze.*`, so treat them as "what this build assumes" rather
// than as constants of nature — ask backend before designing around a number.

/**
 * How many photos go into ONE request. **This is the round-trip knob.**
 *
 * The endpoint takes 1–5 images and decides for itself how many products the
 * set depicts, so this trades calls against grouping:
 *
 *   1  one call per photo. Most calls, but a photo can only ever produce its
 *      own products — two shots of the same car become two drafts.
 *   4  the current default. Four photos, one call, and the model may group them.
 *   5  fewest calls; matches the server cap below.
 *
 * Must not exceed the backend's `openai.image-analyze.max-images` (default 5) —
 * raise that property first if you want more here. Requests are also capped at
 * ANALYZE_MAX_REQUEST_BYTES, which 5 × 5 MB still fits under.
 *
 * A batch is split into consecutive chunks of this size and the chunks run one
 * after another, so ANALYZE_BATCH_SIZE 5 with this at 4 sends 4 then 1.
 */
export const ANALYZE_IMAGES_PER_CALL = 4;

/** How many photos group under one batch card in the UI, and therefore under
 *  one "Analyse" button. Independent of ANALYZE_IMAGES_PER_CALL above — this is
 *  the unit a curator triggers, that one is the unit that travels. */
export const ANALYZE_BATCH_SIZE = 5;
/** 5 MB per file. */
export const ANALYZE_MAX_FILE_BYTES = 5 * 1024 * 1024;
/** JPEG and PNG only — WebP is rejected server-side. Typed as readonly string[]
 *  rather than a literal tuple so `.includes(file.type)` type-checks without a
 *  cast at the one place it's used. */
export const ANALYZE_ACCEPTED_MIME_TYPES: readonly string[] = [
  "image/jpeg",
  "image/png",
];
/** 30 MB per request. Only reachable once a batch sends >1 image at a time. */
export const ANALYZE_MAX_REQUEST_BYTES = 30 * 1024 * 1024;

/** Ours, not the backend's: how many photos one curator session may hold,
 *  across all batches. Two full batches is the shape the flow was designed
 *  around. */
export const ANALYZE_MAX_IMAGES = 50;


/**
 * Names dropped from `missingRequiredFields` before the review screen shows it.
 *
 * `sellerId` is not required and the create path never reads it from the
 * payload: `ProductService.createProduct` derives it from the auth context only
 * for a personal listing (`businessId == null`), so a business product — all
 * this flow makes — is stored without one by design.
 *
 * The backend has been fixed not to report it, and this filter still stays. A
 * deployed build one version behind will keep sending it, and putting a
 * required-looking UUID box in front of a curator for a value that would be
 * discarded is a worse failure than quietly ignoring an extra list entry.
 */
export const ANALYZE_IGNORED_REQUIRED_FIELDS: readonly string[] = ["sellerId"];

/** Below this, a product card gets a visible low-confidence flag. */
export const ANALYZE_LOW_CONFIDENCE = 0.5;

/**
 * The literal the model writes into `details` when it refuses to guess. It is a
 * marker, NOT a value — render it greyed or as placeholder text in an empty
 * field so a curator can see at a glance what still needs filling.
 */
export const ANALYZE_REFUSED_VALUE = "Contact owner for this info";

/** Every draft comes back at this price with displayPrice false; the endpoint
 *  deliberately prices nothing. Treat it as "unpriced", not as ₹100. */
export const ANALYZE_PLACEHOLDER_PRICE = 100;
